'use client'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import styled from 'styled-components'
import { screenSizes } from '@shipt/design-system-themes'
import { getRetailerSearchUrl } from '@shared/constants/routes'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { Button } from '@shipt/design-system-buttons'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { Body, Headline } from '@shipt/design-system-typography'
import { Box } from '@shipt/design-system-layouts'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { useUserShoppingStore } from '@/features/shop/services/ShoppingStore/hooks'

type Props = {
  text: string
  subText?: string
  hideButton?: boolean
  buttonText?: string
  buttonAction?: () => void
  imageInfo?: {
    src: string
    alt: string
    width?: number
    height?: number
  }
  emptyStateRoute?: string
  /** renders the button as a link to this destination instead of pushing a route on click */
  href?: string
  className?: string
}

export const EmptyState = ({
  text,
  className,
  subText = '',
  hideButton = false,
  buttonText = 'Continue shopping',
  buttonAction,
  imageInfo = {
    src: objectStorageUrl('emptyStateBox.webp'),
    alt: 'Empty box',
  },
  emptyStateRoute,
  href,
}: Props) => {
  const router = useSegwayRouter()
  const { data: shoppingStore } = useUserShoppingStore()
  const { src, alt, width = 160, height = 160 } = imageInfo

  const handleButtonClick = () => {
    if (buttonAction) {
      buttonAction()
    } else {
      const route = emptyStateRoute ?? getRetailerSearchUrl(shoppingStore?.name)

      trackElementClicked({
        next_location: route,
        content: buttonText,
      })
      router.push(route)
    }
  }

  return (
    <EmptyStateContainer className={className}>
      <EmptyStateImage
        src={src}
        alt={alt}
        width={width}
        height={height}
        style={{ objectFit: 'contain' }}
      />
      <HeaderText as="h2">{text}</HeaderText>
      <Box marginBottom="lg">
        <SubText align="center" variant="secondary">
          {subText}
        </SubText>
      </Box>
      {!hideButton &&
        (href ? (
          <ButtonLink
            fullWidth
            href={href}
            onClick={() => trackElementClicked({ next_location: href, content: buttonText })}
          >
            {buttonText}
          </ButtonLink>
        ) : (
          <Button fullWidth onClick={handleButtonClick}>
            {buttonText}
          </Button>
        ))}
    </EmptyStateContainer>
  )
}

const EmptyStateContainer = styled.div`
  text-align: center;
  display: flex;
  max-width: 21.5rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 1rem auto;

  @media ${screenSizes.tablet} {
    margin: 2rem auto;
  }
`

const EmptyStateImage = styled(Image)`
  margin-bottom: 1rem;
`

const HeaderText = styled(Headline)`
  margin-bottom: 0.5rem;
`
const SubText = styled(Body)`
  max-width: 21.5rem;
  white-space: pre-line;
`
